import "../common/elements/Header.scss";
import translate from "@/i18n/translate";
import Header from "@/features/common/elements/Header";
import OrgDetailContent from "@/features/common/elements/OrgDetailContent";
import { useContext, useEffect, useReducer } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import PreviewModal from "../lightcast/PreviewModal";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import {
  Organization,
  Institution,
  defaultOrganization
} from "../common/interfaces/Interface";
import { checkIfFsProductIntegrated, checkIfCesProductIntegrated } from "../common/helper";
import { sendRequest } from "../../common/http";
import { useParams } from "react-router-dom";

import Loader from "../common/elements/Loader";
import ApiError from "../common/elements/ApiError";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIntl } from 'react-intl';


interface Action {
  type: string;
  organization: Organization;
  component: string;
}

const defaultAction: Action = {
  type: "",
  organization: defaultOrganization,
  component: ""
};
interface BodyState {
  organization: Organization;
  showMainLoader: boolean;
  apiError: boolean;
}

const reducer = (state: BodyState, action: Action) => {
  switch (action.type) {
    case "setOrganization":
      return {
        ...state,
        organization: action.organization,
        showMainLoader: false
      };
    case "setApiError":
      return {
        ...state,
        apiError: true,
        showMainLoader: false
      };

    default:
      return state;
  }
};

interface UrlParams {
  institution_uuid: string;
  organization_uuid: string;
}

const initialState: BodyState = {
  organization: defaultOrganization,
  showMainLoader: true,
  apiError: false
};

export interface OrgDetailComponentProps {
  setSelectedTabView: (tabId: string) => void;
  auth: boolean;
  productView: "navigator" | "planning";
}

export default function OrgDetailComponent({
  setSelectedTabView,
  auth,
  productView
}: OrgDetailComponentProps): JSX.Element {
  const { institution, integratedProducts } = useContext<AppContextInterface>(AppContext);
  const intl = useIntl();

  const { institution_uuid: institutionUuid, organization_uuid: orgUuid } =
    useParams<UrlParams>();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Use dynamic title based on organization name when available
  const pageTitle = state.organization?.name
    ? `${state.organization.name} - ${intl.formatMessage({ id: 'app.pageTitle.organizationInsights' })}`
    : intl.formatMessage({ id: 'app.pageTitle.organizationInsights' });

  usePageTitle(pageTitle);

  useEffect(() => {
    if (institutionUuid && orgUuid && institution) {
      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setApiError"
        });
      };

      if (productView === "navigator") {
        const applyResult = (result: Organization) => {
          dispatch({
            ...defaultAction,
            type: "setOrganization",
            organization: result
          });
        };

        sendRequest(
          {
            uri: `/org_insights/institution/${institutionUuid}/organization/${orgUuid}/organization_info`,
            camelize: true
          },
          applyResult,
          onError
        );
      } else if (productView === "planning") {
        const applyResult = (result: Organization) => {
          dispatch({
            ...defaultAction,
            type: "setPssOrganization",
            organization: result
          });
        };

        sendRequest(
          {
            uri: `/org_insights/pss/organization/${orgUuid}`,
            camelize: true
          },
          applyResult,
          onError
        );
      }
    }
  }, [institutionUuid, orgUuid]);

  let displayName = (state.organization && state.organization.name) || (institution && institution.name);
  return (
    <>
      {state.showMainLoader && <Loader />}
      {state.apiError ? (
        <ApiError />
      ) : (
        <>
          {auth ? (
            <main id="maincontent" className="main-body-container-home">
              <Header displayName={displayName} productView={productView} />

              <div className="insights-hub-body flex-row">
                <div id="main-org-profile-container" className="main-org-profile">
                  <div>
                    <div className="page-banner -insights-header insights-header-home">
                      <h2 className="titles">Insights</h2>
                      <div className="description">
                        {translate("app.header.description")}
                      </div>
                    </div>
                    {(checkIfFsProductIntegrated(integratedProducts) || checkIfCesProductIntegrated(integratedProducts)) ? (
                      <div className="body -height" id="tab-components">
                        <OrgDetailContent setSelectedTabView={setSelectedTabView} />
                      </div>
                    ) : (
                      <EmptyState institution={institution} productView={productView} />
                    )}
                  </div>
                </div>
              </div>
              <PreviewModal></PreviewModal>
            </main>
          ) : (
            <UnauthorizedComponent />
          )}
        </>
      )}
    </>
  );
}

interface EmptyStateProps {
  institution: Institution | null;
  productView: "navigator" | "planning";
}

const EmptyState = ({
  institution,
  productView
}: EmptyStateProps) => {
  return (
    <div className="body" id="tab-components">
      <section
        className={
          productView === "navigator"
            ? "body wm-grid tab-body"
            : "body wm-grid tab-body-home"
        }
        id="section_insights"
      >
        <div className="empty-state-content-no-fs">
          <div className="empty-state-title">
            {translate("app.noFsIntegration.title")}
          </div>
          <div className="empty-state-description">
            {translate("app.noFsIntegration.description")}
          </div>
        </div>
      </section>
    </div>
  );
}
